import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Dialog, DialogActions, DialogContent, TextField, Select, MenuItem, IconButton, Grid, Tooltip, FormControl, InputLabel, DialogTitle
} from '@mui/material';
import { createMachineAction, getMachineAction, getRawMaterialsAction, rawMaterialMachineMappingAction, clearRawMaterialMachineMappingAction, clearCreateMachineAction } from '../../store/actions';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

const MachineManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [productModel, setProductModel] = useState('');
  const [estimateDay, setEstimateDay] = useState('');
  const [step, setStep] = useState(1);
  const [productId, setProductId] = useState(null);
  const [searchText, setSearchText] = useState('');

  const { successData: createdMachine } = useSelector((state) => state.postMachineReducer);
  const { successData: getMachines } = useSelector((state) => state.getMachineReducer);
  const { successData: rawMaterialsData } = useSelector((state) => state.getRawMaterialsReducer);
  const { successData: rawMaterialMachineMapping } = useSelector((state) => state.rawMaterialMachineMappingReducer);

  // create machine
  const handleCreate = () => {
    dispatch(createMachineAction({
      name: productModel,
      edp: estimateDay,
    }));
  };

  //Get Machine Details
  const handleEdit = (machineId) => {
    // Navigate to edit page with the machine ID
    navigate('/admin/machine-raw-material-mapping', { state: { machineId } });
  };

  const handleAddFields = () => {
    setRawMaterials([...rawMaterials, { rawMaterial_id: '', quantity: '' }]);
  };

  const handleFieldChange = (index, field, value) => {
    const updatedRawMaterials = [...rawMaterials];
    updatedRawMaterials[index][field] = value;
    setRawMaterials(updatedRawMaterials);
  };

  // create machine and raw material mapping
  const handleSave = () => {
    dispatch(rawMaterialMachineMappingAction({
      machine_id: productId,
      raw_materials: rawMaterials?.map(({ rawMaterial, quantity }) => ({
        raw_material_id: rawMaterial,
        quantity
      })),
    }));

    handleClose();
  };

  useEffect(() => {
    if (createdMachine) {
      toast.success('Machine created successfully');
      setStep(2);
      setProductId(createdMachine?.data.id);
      dispatch(clearCreateMachineAction());
    }
  }, [createdMachine]);

  useEffect(() => {
    dispatch(getMachineAction());
  }, [dispatch, rawMaterialMachineMapping, createdMachine]);

  useEffect(() => {
    dispatch(getRawMaterialsAction());
  }, [dispatch])

  useEffect(() => {
    if (rawMaterialMachineMapping) {
      toast.success('Raw materials added successfully');
      resetForm();
      dispatch(clearRawMaterialMachineMappingAction());
    }
  }, [rawMaterialMachineMapping]);

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setProductModel('');
    setEstimateDay('');
    setRawMaterials([]);
  };

  const handleResetField = (index) => {
    const updatedRawMaterials = rawMaterials.filter((_, i) => i !== index);
    setRawMaterials(updatedRawMaterials);
  };

  return (
    <Box sx={{ p: 3, width: { xs: '85%', sm: '75%' }, ml: { xs: '0%', sm: '10%',md:'20%' }, mt: { xs: '15%', sm: '10%',md:'7%' } }}>
      <Box>
        <h1 style={{ fontWeight: "bold", fontSize: "1.8rem", fontFamily: "Poppins" }}>
          Product Management
        </h1>
      </Box>

      <Box mb={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={() => {
            setOpen(true);
          }}
          sx={{
            backgroundColor: '#1814F3',
            color: '#fff',
            borderRadius: '5px',
            fontFamily: 'Inter',
            padding: { xs: '8px 16px', sm: '10px 20px' },
          }}
        >
          Create Product
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: '5%' }}>
        <Table
          sx={{
            width: { xs: '100%', sm: '90%', md: '60%' },
            margin: 'auto',
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#B5B7C0", fontFamily: 'Poppins' }}>Product</TableCell>
              <TableCell sx={{ color: "#B5B7C0", fontFamily: 'Poppins' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(getMachines?.data) ? (
              getMachines.data.map((machine) => (
                <TableRow key={machine.id}>
                  <TableCell>{machine.name}</TableCell>
                  <TableCell>
                    <Button onClick={() => handleEdit(machine.id)}>Update</Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No products available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Machine Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {step === 1 ? 'Create Product' : 'Add Raw Materials'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product and Model"
                margin="normal"
                value={productModel}
                onChange={(e) => setProductModel(e.target.value)}
                disabled={step === 2}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimate Days"
                type="number"
                margin="normal"
                value={estimateDay}
                onChange={(e) => setEstimateDay(e.target.value)}
                disabled={step === 2}
                variant="outlined"
              />
            </Grid>
          </Grid>

          {step === 2 && (
            <>
              {rawMaterials.map((item, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mt={2}
                  sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: '100%',
                  }}
                >
                  <FormControl variant="outlined" sx={{ width: { xs: '100%', sm: '60%' } }}>
                    <InputLabel id={`raw-material-label-${index}`}>Raw Material</InputLabel>
                    <Select
                      labelId={`raw-material-label-${index}`}
                      value={item.rawMaterial || ''}
                      onChange={(e) => handleFieldChange(index, 'rawMaterial', e.target.value)}
                      label="Raw Material"
                      sx={{
                        '& .MuiSelect-select': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      <MenuItem
                        value=""
                        sx={{
                          p: 0,
                          position: 'sticky',
                          top: 0,
                          bgcolor: 'background.paper',
                          zIndex: 1,
                          borderBottom: '1px solid #e0e0e0',
                        }}
                        disableRipple
                        disableTouchRipple
                      >
                        <TextField
                          size="small"
                          autoFocus
                          placeholder="Search raw materials..."
                          fullWidth
                          variant="outlined"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            const searchText = e.target.value.toLowerCase();
                            setSearchText(searchText);
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape' && e.key !== 'Tab') {
                              e.stopPropagation();
                            }
                          }}
                          sx={{ m: 1 }}
                        />
                      </MenuItem>
                      {rawMaterialsData?.data
                        ?.slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter((material) =>
                          !searchText || material.name.toLowerCase().includes(searchText.toLowerCase())
                        )
                        .map((rawMaterial) => (
                          <MenuItem
                            key={rawMaterial.id}
                            value={rawMaterial.id}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {rawMaterial.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleFieldChange(index, 'quantity', e.target.value)}
                    sx={{ width: { xs: '100%', sm: '30%' } }}
                    variant="outlined"
                  />
                  <Tooltip title="Reset">
                    <IconButton onClick={() => handleResetField(index)}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Tooltip title="Add Raw Material">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddFields}
                    startIcon={<AddIcon />}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    Add Raw Material
                  </Button>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          {step === 1 ? (
            <Button
              onClick={handleCreate}
              color="primary"
              disabled={!productModel || !estimateDay}
            >
              Create
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              color="primary"
              disabled={rawMaterials.length === 0 || rawMaterials.some((item) => !item.rawMaterial || !item.quantity)}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MachineManagement;
